import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { DEFAULT_CENTER } from "@/lib/geo";

// markers: [{ key, lat, lng, color }]
export function GeoMap({ token, markers = [], routeGeo, className }) {
  const ref = useRef();
  const map = useRef();
  const store = useRef({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!token || map.current || !ref.current) return;
    mapboxgl.accessToken = token;
    // Centre on the first marker so the map never has to jump (a mid-load jump
    // aborts the initial tiles and can leave the map showing only land colour).
    const first = markers.find((m) => m.lat != null && m.lng != null);
    const rc0 = routeGeo?.coordinates?.[0];
    const start = first ? [first.lng, first.lat] : (rc0 || DEFAULT_CENTER);
    map.current = new mapboxgl.Map({
      container: ref.current, style: "mapbox://styles/mapbox/streets-v12",
      center: start,
      zoom: (first || rc0) ? 14 : 12, attributionControl: false,
    });
    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.current.on("load", () => { map.current.resize(); setReady(true); });
    map.current.on("error", (e) => { if (e?.error) console.warn("Mapbox:", e.error.message || e.error); });
    // A map created before its container has size renders as a solid block until
    // resized — watch the container and resize whenever its dimensions change.
    const ro = new ResizeObserver(() => map.current && map.current.resize());
    ro.observe(ref.current);
    return () => { ro.disconnect(); map.current?.remove(); map.current = null; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return;
    const keep = new Set();
    markers.forEach((mk) => {
      if (mk.lat == null || mk.lng == null) return;
      keep.add(mk.key);
      if (store.current[mk.key]) store.current[mk.key].setLngLat([mk.lng, mk.lat]);
      else store.current[mk.key] = new mapboxgl.Marker({ color: mk.color }).setLngLat([mk.lng, mk.lat]).addTo(m);
    });
    Object.keys(store.current).forEach((k) => {
      if (!keep.has(k)) { store.current[k].remove(); delete store.current[k]; }
    });
    const pts = markers.filter((mk) => mk.lat != null && mk.lng != null).map((mk) => [mk.lng, mk.lat]);
    if (pts.length >= 2) {
      const b = pts.reduce((bb, c) => bb.extend(c), new mapboxgl.LngLatBounds(pts[0], pts[0]));
      m.fitBounds(b, { padding: 70, maxZoom: 15, duration: 600 });
    } else if (pts.length === 1) {
      m.easeTo({ center: pts[0], zoom: 14, duration: 600 });
    }
  }, [markers, ready]);

  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return;
    if (m.getLayer("route")) m.removeLayer("route");
    if (m.getSource("route")) m.removeSource("route");
    if (routeGeo) {
      m.addSource("route", { type: "geojson", data: { type: "Feature", geometry: routeGeo } });
      m.addLayer({ id: "route", type: "line", source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#2563eb", "line-width": 4 } });
      const coords = routeGeo.coordinates || [];
      if (coords.length >= 2) {
        const b = coords.reduce((bb, c) => bb.extend(c), new mapboxgl.LngLatBounds(coords[0], coords[0]));
        m.fitBounds(b, { padding: 60, maxZoom: 15, duration: 600 });
      }
    }
  }, [routeGeo, ready]);

  return <div ref={ref} data-testid="geo-map" className={className || "h-72 w-full overflow-hidden rounded-2xl border border-border sm:h-[26rem]"} />;
}
